// OpenAIAssistant.js
import { toast } from 'react-toastify';
import { BASE_DIRECTIVE, MODEL, VOICE } from './Prompts.js';
import { ToolsRegistry } from './ToolRegistry';
import { ContextRegistry } from './ContextRegistry';

export class OpenAIAssistant {
  constructor(
    streamingTranscriptionInstance,
    apiClient,
    deviceId = null,
    onCommandSuccess = null,
  ) {
    this.MODEL = MODEL;
    this.VOICE = VOICE;
    // System directive must be provided via setSystemDirective before use
    this.SYSTEM_DIRECTIVE = BASE_DIRECTIVE;

    this.streamingTranscriptionInstance = streamingTranscriptionInstance;
    this.apiClient = apiClient;
    this.deviceId = deviceId;
    this.onCommandSuccess = onCommandSuccess;

    this.isSessionActive = false;
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.mediaStream = null;
    this.ephemeralKey = null;
    this.speechInterval = null;

    // Instantiate the ToolsRegistry to manage tools
    this.toolsRegistry = new ToolsRegistry();

    // Instantiate the ContextRegistry to manage dynamic context/memory
    this.contextRegistry = new ContextRegistry();

    // Bind tool methods to the instance
    this.addTools = this.addTools.bind(this);
    this.removeTools = this.removeTools.bind(this);

    // Bind context methods to the instance
    this.addContext = this.addContext.bind(this);
    this.removeContext = this.removeContext.bind(this);
  }

  /**
   * Update the system directive (verbosity / behavior) at runtime.
   * If a session is already active, this will immediately send a session.update
   * so the new instructions take effect without restarting.
   * @param {string} systemDirective
   */
  setSystemDirective(systemDirective) {
    if (!systemDirective || typeof systemDirective !== 'string') {
      console.warn('Ignoring invalid system directive update');
      return;
    }
    this.SYSTEM_DIRECTIVE = systemDirective;
    if (this.isSessionActive) {
      this.sendClientEvent(this.getSessionUpdatePayload());
    }
  }

  /**
   * Initializes the session by:
   * - Have our BE create a realtime session and return an ephemeral key (reference to it)
   * - Creating the RTCPeerConnection and data channel
   * - Getting the microphone audio and adding it to the connection (muted)
   * - Performing the SDP offer/answer handshake with OpenAI's endpoint
   */
  async init() {
    // Fetch a session and get the ephemeral key from the backend (server holds real API key)
    // If a key was already fetched (e.g., in parallel with Picovoice), skip the network call.
    if (!this.ephemeralKey) {
      try {
        const keyResponse =
          await this.apiClient.getSynapseKey('openai_realtime');
        console.log('Key response:', keyResponse);
        if (!keyResponse || !keyResponse.accessKey) {
          console.error('Failed to obtain OpenAI ephemeral key', keyResponse);
          toast.error('Failed to initialize AI assistant (no key)');
          return;
        }
        // Backend returns the ephemeral/client secret as `accessKey`
        this.ephemeralKey = keyResponse.accessKey;
      } catch (error) {
        console.error(
          'Error fetching OpenAI ephemeral key from backend:',
          error,
        );
        toast.error('Failed to initialize AI assistant');
        return;
      }
    }

    // Create the RTCPeerConnection and set up event handlers
    this.peerConnection = new RTCPeerConnection();
    this.audioElement = document.createElement('audio');
    this.audioElement.autoplay = true;
    this.peerConnection.ontrack = (event) => {
      // When a remote audio track is received, attach it to the audio element
      this.audioElement.srcObject = event.streams[0];
    };

    // Create a data channel for sending events
    this.dataChannel = this.peerConnection.createDataChannel('oai-events');

    // Set up the onmessage handler so that any event from the server
    // will automatically be passed to processIncomingResponse.
    this.dataChannel.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        // console.info("Received message from server:", message);
        this.processIncomingResponse(message);
      } catch (err) {
        console.error('Error parsing incoming message:', err);
      }
    };

    // Get audio from the user's microphone - use exact to force the selected device
    try {
      const audioConstraints =
        this.deviceId
          ? { deviceId: { exact: this.deviceId } }
          : true;

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone');
      return;
    }

    // Mute the audio track initially
    const audioTrack = this.mediaStream.getAudioTracks()[0];
    audioTrack.enabled = false; // Start muted

    // Add the audio track to the peer connection
    this.peerConnection.addTrack(audioTrack, this.mediaStream);

    // Create and set the local SDP offer
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);

    // Send the SDP offer to OpenAI's endpoint for a remote SDP answer
    const sdpResponse = await fetch(
      'https://api.openai.com/v1/realtime/calls',
      {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${this.ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      },
    );
    const answerSdp = await sdpResponse.text();
    const answer = { type: 'answer', sdp: answerSdp };
    await this.peerConnection.setRemoteDescription(answer);

    // Wait until the data channel is open
    await new Promise((resolve) => {
      if (this.dataChannel.readyState === 'open') {
        resolve();
      } else {
        this.dataChannel.onopen = () => {
          console.info('Data channel opened');
          resolve();
        };
      }
    });

    this.isSessionActive = true;
    console.info('AI Session initialized with muted audio');

    // Send an initial session update with the current tools and context.
    this.sendClientEvent(this.getSessionUpdatePayload());
    return;
  }

  /**
   * Unmutes the audio track so that the microphone audio is sent to the connection.
   */
  async startListening() {
    if (this.mediaStream) {
      const audioTrack = this.mediaStream.getAudioTracks()[0];
      audioTrack.enabled = true;
      console.info('Audio track unmuted');
    } else {
      toast.error('No media stream available.');
      console.error('No media stream available.');
    }
    return;
  }

  /**
   * Mutes the audio track so that no microphone audio is sent.
   */
  stopListening() {
    if (this.mediaStream) {
      const audioTrack = this.mediaStream.getAudioTracks()[0];
      audioTrack.enabled = false;
      console.info('Audio track muted');
    } else {
      toast.warn('No active media stream to mute.');
      console.warn('No active media stream to mute.');
    }
  }

  /**
   * Cancels any in-progress response from the AI.
   * This will interrupt the AI if it's currently speaking.
   */
  cancelResponse() {
    if (this.isSessionActive && this.dataChannel) {
      const cancelEvent = {
        type: 'response.cancel',
      };
      this.sendClientEvent(cancelEvent);
      this.sendTextMessage('Stop Talking.');
      console.info('Response cancellation requested');
    }
  }

  /**
   * Fully terminates the session by closing the data channel and the RTCPeerConnection.
   */
  stopSession() {
    // Prevent multiple stop calls
    if (!this.isSessionActive) {
      return;
    }

    this.isSessionActive = false;

    // Ensure audio capture is stopped
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    console.info('AI Session disconnected');
  }

  /**
   * Sends a JSON event through the data channel, if open.
   */
  sendClientEvent(message) {
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      message.event_id = message.event_id || crypto.randomUUID();
      this.dataChannel.send(JSON.stringify(message));
      console.info(`Event sent: ${message.type}`);
    } else {
      console.error(
        'Failed to send message - no data channel available',
        message,
      );
    }
  }

  /**
   * Helper for sending a text message.
   */
  sendTextMessage(message, requestResponse = false) {
    // Other Types include "input_attachments", "input_command", "input_query"
    const event = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: message }],
      },
    };
    this.sendClientEvent(event);
    if (requestResponse) {
      this.sendClientEvent({ type: 'response.create' });
    }
  }

  /* ===== Tool Management and Function-Call Handling ===== */

  /**
   * Adds one or more tools and immediately updates the session.
   * If a tool with the same name exists, it will be replaced.
   * @param {Array<Object>|Object} tools - A list of tool definitions or a single tool object with optional `handler` functions.
   */
  addTools(tools) {
    console.log('Adding tools:', tools);
    if (Array.isArray(tools)) {
      tools.forEach((tool) => this.toolsRegistry.addTool(tool));
    } else {
      this.toolsRegistry.addTool(tools);
    }
    if (this.isSessionActive) {
      this.sendClientEvent(this.getSessionUpdatePayload());
    }
  }

  /**
   * Removes one or more tools by name and immediately updates the session.
   * @param {string|Array<string>} toolNames - The name of the tool or a list of tool names to remove.
   */
  removeTools(toolNames) {
    console.log('Removing tools:', toolNames);
    if (Array.isArray(toolNames)) {
      toolNames.forEach((toolName) => this.toolsRegistry.removeTool(toolName));
    } else {
      this.toolsRegistry.removeTool(toolNames);
    }
    if (this.isSessionActive) {
      this.sendClientEvent(this.getSessionUpdatePayload());
    }
  }

  /* ===== Context/Memory Management ===== */

  /**
   * Adds or updates context for the AI session.
   * If a context with the same ID exists, it will be replaced.
   * @param {string} contextId - A unique identifier for this context (e.g., 'current_patient')
   * @param {Object} contextData - The context data to store
   */
  addContext(contextId, contextData) {
    console.log('Adding context:', contextId, contextData);
    this.contextRegistry.addContext(contextId, contextData);
    if (this.isSessionActive) {
      this.sendClientEvent(this.getSessionUpdatePayload());
    }
  }

  /**
   * Removes a context by identifier and immediately updates the session.
   * @param {string} contextId - The context identifier to remove
   */
  removeContext(contextId) {
    console.log('Removing context:', contextId);
    this.contextRegistry.removeContext(contextId);
    if (this.isSessionActive) {
      this.sendClientEvent(this.getSessionUpdatePayload());
    }
  }

  /**
   * Generates the session update payload with both tools and context.
   */
  getSessionUpdatePayload() {
    const contextString = this.contextRegistry.getFormattedContext();
    const instructions = this.SYSTEM_DIRECTIVE + contextString;

    return {
      type: 'session.update',
      session: {
        type: 'realtime',
        model: this.MODEL,
        audio: {
          output: { voice: this.VOICE },
        },
        instructions: instructions,
        tools: this.toolsRegistry.getAllTools(),
        tool_choice: 'auto',
      },
    };
  }

  /**
   * Processes an incoming response event. If it contains a function call,
   * this method will look up the tool by name, invoke its handler with the
   * parsed arguments, and optionally send back a response with the result.
   *
   * @param {Object} event - The event received from the realtime API.
   */
  async processIncomingResponse(event) {
    if (
      event.type === 'response.done' &&
      event.response &&
      event.response.output
    ) {
      for (const output of event.response.output) {
        if (output.type === 'function_call') {
          const tool = this.toolsRegistry.getTool(output.name);
          if (tool && typeof tool.handler === 'function') {
            try {
              // Parse the arguments passed in the function call.
              const args = JSON.parse(output.arguments);
              const result = await tool.handler(args);
              console.info(
                `Tool "${output.name}" executed with result:`,
                result,
              );
              // Clear grace period so silence detection resumes immediately
              if (
                typeof this.streamingTranscriptionInstance.clearGracePeriod ===
                'function'
              ) {
                this.streamingTranscriptionInstance.clearGracePeriod();
              }
              // Trigger success callback with tool name
              if (
                this.onCommandSuccess &&
                typeof this.onCommandSuccess === 'function'
              ) {
                this.onCommandSuccess(output.name);
              }
              // [BROKEN] Optionally, send back a response event with the result.
              // This could come in handy for getting information from out backend...
              // if (result) {
              //   this.sendClientEvent({
              //     type: "conversation.item.create",
              //     item: {
              //       type: "function_call.response",
              //       name: output.name,
              //       result: result,
              //       },
              //   });
              // }
            } catch (error) {
              console.error(`Error executing tool "${output.name}":`, error);
            }
          } else {
            console.warn(`No handler defined for tool "${output.name}"`);
          }
        }
      }
    } else if (event.type === 'output_audio_buffer.started') {
      // AI started speaking - keep the silence timer fresh
      this.streamingTranscriptionInstance.lastSpeechTimestamp = Date.now();
      if (!this.speechInterval) {
        // Update frequently (100ms) to prevent silence detection during AI speech
        this.speechInterval = setInterval(() => {
          this.streamingTranscriptionInstance.lastSpeechTimestamp = Date.now();
        }, 100);
      }
    } else if (event.type === 'output_audio_buffer.stopped') {
      // AI stopped speaking - clear grace period so silence detection kicks in
      if (
        typeof this.streamingTranscriptionInstance.clearGracePeriod ===
        'function'
      ) {
        this.streamingTranscriptionInstance.clearGracePeriod();
      } else {
        // Fallback if clearGracePeriod not available
        this.streamingTranscriptionInstance.lastSpeechTimestamp = Date.now();
      }
      clearInterval(this.speechInterval);
      this.speechInterval = null;
    } else {
      // console.info('Event received:', event);
    }
  }
}

export default OpenAIAssistant;
